<!--
  The upgrade call for a member with no paid access, above the profile block.
  It replaces the dark upgrade bar when the sidebar is on, with the same three
  texts: a team owner finishes the team plan, a team member asks the owner,
  everybody else upgrades to Pro. SidebarNoticeCard decides when it shows and
  names the text (useShellNotices).
-->
<template>
  <InertiaLink
    v-tooltip="{ content: 'Upgrade', placement: 'right', disabled: !isRail }"
    :href="target.href"
    class="gm-sb-upgrade"
    @click="trackClick"
  >
    <SparklesIcon class="gm-sb-upgrade-icon" aria-hidden="true" />
    <span class="gm-sb-upgrade-body">
      <span class="gm-sb-upgrade-faces" aria-hidden="true">
        <img
          v-for="face in FACES"
          :key="face"
          :src="face"
          alt=""
          loading="lazy"
        />
      </span>
      <span v-if="target.lead">
        <b>{{ target.lead }}</b>
        {{ target.text }}
      </span>
      <span v-else>{{ target.text }}</span>
      <span class="gm-sb-upgrade-go">
        <span>{{ target.action }}</span>
        <ArrowRightIcon aria-hidden="true" />
      </span>
    </span>
  </InertiaLink>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SparklesIcon from '~icons/lucide/sparkles'
import ArrowRightIcon from '~icons/lucide/arrow-right'
import type { UpgradeKind } from './useShellNotices'

const props = defineProps<{ isRail: boolean; kind: UpgradeKind }>()

const FACES = [
  '/blog-mosaic-faces/eae2ab7c-289b-4ee9-8cfc-9a3552cf9951.webp',
  '/blog-mosaic-faces/ammarah-ahmed.webp',
  '/blog-mosaic-faces/growthgal.webp',
]

const target = computed(() => {
  if (props.kind === 'owner') {
    return {
      href: '/upgrade_team_plan/new',
      lead: '',
      text: "Your team doesn't have a paid subscription.",
      action: 'Complete your team plan',
    }
  }
  if (props.kind === 'member') {
    return {
      href: '/settings/billing',
      lead: '',
      text: "Your team doesn't have a paid subscription.",
      action: 'Ask your owner to upgrade, or leave the team',
    }
  }
  return {
    href: '/upgrade_plan/new',
    // No price (Foti, 28 Oct): the plans page shows it.
    lead: 'Membership:',
    text: 'unlimited 1:1 calls with mentors.',
    action: 'See plans',
  }
})

type Analytics = {
  track?: (event: string, props: object) => void
  userType?: () => string
}

// The same event as the upgrade bar, so the funnel reports keep one source.
const trackClick = () => {
  if (target.value.href !== '/upgrade_plan/new') return
  const analytics = (window as unknown as { segmentAnalytics?: Analytics })
    .segmentAnalytics
  analytics?.track?.('Upgrade to Pro link Clicked', {
    user_type: analytics.userType?.(),
    link_source: 'Upgrade to pro sidebar card',
  })
}
</script>
